import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { MapPin, ArrowLeft, Loader2, Save, Users, ExternalLink, Info } from 'lucide-react'
import { useMutation, useQuery } from 'react-query'
import { areasService } from '@/services/areas'
import { brandsService } from '@/services/brands'
import { usersService } from '@/services/users'
import { NavigationUtils } from '@/utils/navigation'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'

const AreaCreate = () => {
  const navigate = useNavigate()
  const { hasRole } = useAuth()

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand_id: '',
    is_active: true
  })

  const [errors, setErrors] = useState({})

  // Check permissions using useEffect
  useEffect(() => {
    if (!hasRole('admin') && !hasRole('manager')) {
      NavigationUtils.navigateBackToSettings(navigate, 'areas')
    }
  }, [navigate, hasRole])

  // Fetch brands for dropdown
  const { data: brandsData, isLoading: brandsLoading } = useQuery(
    ['brands'],
    () => brandsService.getAll({ page_size: 1000 }),
    {
      onError: (error) => {
        toast.error('Failed to load brands')
        console.error('Error loading brands:', error)
      }
    }
  )

  // Note: Area manager assignment is now handled through user management
  // Users with area manager roles can be assigned to areas via the user creation/editing forms

  // Create mutation
  const createMutation = useMutation(
    (areaData) => areasService.create(areaData),
    {
      onSuccess: (data) => {
        toast.success('Area created successfully')
        navigate(`/areas/${data.id}`) // Navigate to area detail page
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail || 'Failed to create area'
        toast.error(errorMessage)
        
        // Handle validation errors
        if (error.response?.data?.validation_errors) {
          setErrors(error.response.data.validation_errors)
        }
      }
    }
  )

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Area name is required'
    }

    if (!formData.brand_id) {
      newErrors.brand_id = 'Brand is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const submitData = {
      ...formData,
      brand_id: parseInt(formData.brand_id)
    }

    createMutation.mutate(submitData)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'areas')}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Areas
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Area</h1>
            <p className="mt-1 text-sm text-gray-600">
              Add a new organizational area to the system
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Area Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Basic area details and configuration
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Area Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Area Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter area name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter area description (optional)"
              />
            </div>

            {/* Brand Selection */}
            <div>
              <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700 mb-2">
                Brand *
              </label>
              <select
                id="brand_id"
                name="brand_id"
                value={formData.brand_id}
                onChange={handleChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.brand_id ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={brandsLoading}
              >
                <option value="">Select a brand</option>
                {brandsData?.brands?.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              {errors.brand_id && (
                <p className="mt-1 text-sm text-red-600">{errors.brand_id}</p>
              )}
              {brandsLoading && (
                <p className="mt-1 text-sm text-gray-500">Loading brands...</p>
              )}
            </div>

            {/* Area Manager Assignment Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">
                    User Assignment Workflow
                  </h3>
                  <div className="text-sm text-blue-700 space-y-2">
                    <p>
                      After creating this area, you'll assign managers and users through the{' '}
                      <strong>User Management</strong> section. This centralized approach ensures
                      consistent permissions and easy management.
                    </p>

                    <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-2">Quick Guide:</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• <strong>Area Manager:</strong> Create/edit user with "Area Manager" role → Select this area</li>
                        <li>• <strong>Regular Users:</strong> Create/edit user with "User" role → Select this area</li>
                        <li>• <strong>Single Manager:</strong> Each area can only have one area manager</li>
                        <li>• <strong>Multiple Users:</strong> Areas can have multiple regular users</li>
                      </ul>
                    </div>

                    <div className="mt-3">
                      <Link
                        to="/settings?tab=users"
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        Go to User Management
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Inactive areas will not be available for user assignment
              </p>
            </div>

          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'areas')}
            className="btn-outline btn-md"
            disabled={createMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Area
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AreaCreate
